import { RefObject, useEffect } from "react";
import useDevice from "../useDevice";

function findClosestSlide(containerRef: HTMLDivElement, slideGap: number) {
  const slides = containerRef.children ?? [];
  const slideWidth = slides[0]?.clientWidth ?? 0;
  const scrollLeft = containerRef.scrollLeft ?? 0;
  const slideIndex = Math.round(scrollLeft / slideWidth);
  const nearestSlide = slideIndex * slideWidth + slideIndex * slideGap;
  return nearestSlide - scrollLeft;
}

function handleDragEnd(container: HTMLDivElement, slideGap: number) {
  const slide = findClosestSlide(container, slideGap);

  container.scrollBy({
    left: slide,
    behavior: "smooth"
  });
  function removeActive() {
    container.classList.remove("active");
  }

  if (slide === 0) {
    removeActive();
    return;
  }

  container.addEventListener("scrollend", removeActive, { once: true });
}

export default function useDesktopDrag(
  containerRef: RefObject<HTMLDivElement>,
  slideGap = 16
) {
  const { desktop } = useDevice();
  useEffect(() => {
    if (!desktop) return;
    if (!containerRef.current) return;
    const container = containerRef.current;

    let isDown = false;
    let hasMoved = false;
    let startX: number;
    let scrollLeft: number;

    const handleDown = (e: MouseEvent) => {
      isDown = true;
      startX = e.pageX - container.offsetLeft;
      scrollLeft = container.scrollLeft;
    };

    const handleLeave = () => {
      isDown = false;
      handleDragEnd(container, slideGap);
    };

    const handleUp = (e: MouseEvent) => {
      if (hasMoved) {
        e.preventDefault();
        hasMoved = false;
      }
      isDown = false;
      handleDragEnd(container, slideGap);
    };
    const handleMove = (e: MouseEvent): void => {
      if (!isDown) return;
      hasMoved = true;
      container.classList.add("active");
      e.preventDefault();
      const x = e.pageX - container.offsetLeft;
      const walk = (x - startX) * 1; //scroll-fast
      const finalScroll = scrollLeft - walk;
      const clamped = clamp(finalScroll, 0, container.scrollWidth);

      container.scrollLeft = clamped;
    };

    container.addEventListener("mousedown", handleDown);
    container.addEventListener("mouseleave", handleLeave);
    container.addEventListener("mouseup", handleUp);
    container.addEventListener("mousemove", handleMove);

    return () => {
      container.removeEventListener("mousedown", handleDown);
      container.removeEventListener("mouseleave", handleLeave);
      container.removeEventListener("mouseup", handleUp);
      container.removeEventListener("mousemove", handleMove);
    };
  }, [containerRef, slideGap, desktop]);
}

const clamp = (num: number, min: number, max: number) => {
  return Math.min(Math.max(num, min), max);
};
